import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, NamedLink, Heading } from '../../../components';

import css from './LoginForm.module.css';
import { prepareToState } from '../../../util/urlHelpers';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const LoginFormComponent = props => {
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileRendered, setTurnstileRendered] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileContainerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!TURNSTILE_SITE_KEY) {
      // No Turnstile configured — allow form submission without it
      setTurnstileRendered(true);
      return;
    }
    if (window.turnstile) {
      setTurnstileLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileLoaded(true);
    script.onerror = () => {
      // Adblocker or CSP blocked the script — allow fallback
      console.warn('Turnstile script failed to load (blocked by adblocker or CSP)');
      setTurnstileRendered(true);
    };
    document.head.appendChild(script);

    // Timeout fallback: if script doesn't load in 10s, allow form anyway
    const timeout = setTimeout(() => {
      if (!turnstileRef.current) {
        console.warn('Turnstile script load timed out');
        setTurnstileRendered(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  // Render the Turnstile widget explicitly when the container is ready
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (!turnstileLoaded || !turnstileContainerRef.current) return;
    // Prevent double-render
    if (turnstileRef.current) return;

    try {
      const widgetId = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        size: 'normal',
      });
      turnstileRef.current = widgetId;
      setTurnstileRendered(true);
    } catch (err) {
      console.error('Turnstile render error:', err);
      setTurnstileRendered(true); // Allow form fallback on render error
    }
  }, [turnstileLoaded]);

  return (
  <FinalForm
    {...props}
    onSubmit={values => {
      // Inject Turnstile token into form values at FinalForm level,
      // before the parent's onSubmit handler receives them. We do this
      // here (not on the <Form> element) so FinalForm's handleSubmit
      // can call event.preventDefault() and prevent the native form
      // POST to "/".
      const turnstileToken =
        TURNSTILE_SITE_KEY && turnstileRef.current
          ? window.turnstile?.getResponse(turnstileRef.current) || undefined
          : undefined;
      return props.onSubmit({ ...values, turnstileToken });
    }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        fromState,
        isIframe,
      } = fieldRenderProps;

      // email
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const turnstileDisabled = TURNSTILE_SITE_KEY && !turnstileRendered;
      const submitDisabled = invalid || submitInProgress || turnstileDisabled;

      let toState = prepareToState(fromState, isIframe);

      const passwordRecoveryLink = (
        <NamedLink name="PasswordRecoveryPage" className={css.recoveryLink}>
          <FormattedMessage id="LoginForm.forgotPassword" />
        </NamedLink>
      );

      const signupLink = (
        <NamedLink name="SignupPage" className={css.recoveryLink} {...toState}>
          <FormattedMessage id="LoginForm.createAccountLink" />
        </NamedLink>
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.loginHeadingContainer}>
            <Heading as="h1" rootClassName={css.loginHeading}>
              Sign In
            </Heading>
          </div>

          <div>
            <FieldTextInput
              className={css.emailField}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={
                <>
                  Email{' '}
                  <span className={css.requiredText}>(required)</span>
                </>
              }
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="current-password"
              label={
                <div>
                  <div>
                    Password{' '}
                    <span className={css.requiredText}>(required)</span>
                  </div>
                  <div className={css.passwordHelper}>
                    Must be at least 8 characters
                  </div>
                </div>
              }
              validate={passwordRequired}
            />

            {/* Honeypot field - hidden from users, catches bots */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
              <FieldTextInput
                type="text"
                id="website"
                name="website"
                placeholder="Leave this field blank"
                tabIndex="-1"
                autoComplete="off"
              />
            </div>
          </div>

          {TURNSTILE_SITE_KEY ? (
            <div className={css.turnstileWrapper}>
              <div ref={turnstileContainerRef} />
            </div>
          ) : null}

          {false ? <p className={css.recoveryLink}>
              <span className={css.recoveryLinkInfo}>
                <FormattedMessage
                  id="LoginForm.forgotPasswordInfo"
                  values={{ passwordRecoveryLink }}
                />
              </span>
            </p> : null}
          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="LoginForm.logIn" />
            </PrimaryButton>

            <p className={css.bottomWrapperText}>
              <span className={css.recoveryLinkInfo}>
                <FormattedMessage
                  id="LoginForm.createAccount"
                  values={{ signupLink }}
                />
              </span>
            </p>
          </div>
        </Form>
      );
    }}
  />
);
};

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
